import { UsersIcon } from '@heroicons/react/16/solid';
import { RadioCheckedIcon } from '@components/common/Icons';
import {
  ListboxStyled, ListboxStyledButton, ListboxStyledOptions, ListboxStyledOption, Chip,
} from '@components/common';
import { ListboxWrapper, ListboxWrapperProps } from '@components/common/Forms';
import { useTranslation } from 'next-i18next';
import { boardType } from '../helpers/filterObjects';
import { filterStyles } from '../helpers/filterStyles';


// Interfaces
// **************************************
type PickWrapperProps = 'recordFunc' | 'submitFunc' | 'idPrefix';
type OrderByBoardTypeProps<DataType> = Pick<ListboxWrapperProps<DataType>, PickWrapperProps> & {
  totalCandidates: number;
};


/**
 * @description The order by group component. It's a stylized ListboxWrapper component for sorting the results.
 * ***recordFunc** - the function to record the selected value.
 * ***submitFunc** - the function to submit the selected value.
 * ***idPrefix** - the prefix for the id of the component. Defaults to an empty string.
 */
export const OrderByBoardType = <DataType extends object>(props: OrderByBoardTypeProps<DataType>) => {
  // Destruction props
  const {
    recordFunc,
    submitFunc,
    idPrefix,
    totalCandidates,
  } = props;

  // Translation
  const { t } = useTranslation('common');

  // Add sort by application date if in job context (jobSlug is provided)
  const { fields } = boardType;


  // Render component
  // **********************************************
  return (
    <ListboxWrapper
      fieldName="board"
      data={fields}
      defaultValueKey="recordOptions.defaultValue"
      labelKey="recordOptions.filtersLabel"
      withTranslation
      recordFunc={recordFunc}
      submitFunc={submitFunc}
      idPrefix={idPrefix}
      renderListbox={({
        selectedLabel, lsData, selected, setSelected,
      }) => (
        <ListboxStyled
          className="relative"
          by="label"
          value={selected}
          onChange={setSelected}
        >
          {({ open }) => (
            <>
              <ListboxStyledButton
                as={Chip}
                icon={<UsersIcon />}
                isDropdownOpen={open}
                withDropdownArrow
              >
                {selectedLabel === t('board.filter.applicants.label.all') && (
                <>
                  {t('board.filter.applicants')}
                  :
                  {' '}
                </>
                )}

                <>
                  {selectedLabel}
                  {selectedLabel === t('board.filter.applicants.label.all') && (
                  <>
                    {' '}
                    (
                    { totalCandidates }
                    )
                  </>
                  )}
                </>

              </ListboxStyledButton>

              <ListboxStyledOptions
                className={`${filterStyles.listboxOptionsWrapper} pb-2`}
                animation="slideDown"
                anchor="bottom start"
              >

                <span className="my-2 block px-3.5 text-sm text-ink-light">{`${t('order-by')}:`}</span>
                {lsData.map((field) => (
                  <ListboxStyledOption
                    key={field.label}
                    value={field}
                    className={`${filterStyles.listboxOption} gap-1.5`}
                  >
                    <RadioCheckedIcon className={filterStyles.listboxOptionCheck} />
                    <>
                      {t(field.label)}
                      {t(field.label) === t('board.filter.applicants.label.all') && (
                      <span>
                        (
                        {totalCandidates}
                        )
                      </span>
                      )}
                    </>
                  </ListboxStyledOption>
                ))}
              </ListboxStyledOptions>

            </>
          )}
        </ListboxStyled>
      )}
    />
  );
};
